import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { AddExpense } from '../components/AddExpense';
import { DonutChart, YearlyBarChart } from '../components/ExpenseChart';
import { ExpenseList } from '../components/ExpenseList';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { expenseAPI, categoryAPI } from '../lib/api';
import { getCurrentMonth, getCurrentYear, getShortMonthName, getMonthName } from '../lib/utils';
import { Loader2, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'yearly'
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [startYear, setStartYear] = useState(2025); // Fixed 5-year range starting from 2025
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Generate 5-year range based on startYear
  const yearRange = Array.from({ length: 5 }, (_, i) => startYear + i);

  const fetchData = useCallback(async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      
      const params = {
        year: selectedYear,
      };
      
      if (viewMode === 'monthly') {
        params.month = selectedMonth;
      }
      
      if (selectedCategory && selectedCategory !== 'all') {
        params.category_id = selectedCategory;
      }
      
      const [expensesRes, categoriesRes, summaryRes] = await Promise.all([
        expenseAPI.getAll(params),
        categoryAPI.getAll(),
        expenseAPI.getSummary({ year: selectedYear }),
      ]);

      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);
      
      if (showRefreshToast) toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [viewMode, selectedMonth, selectedYear, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleExpenseAdded = (expense) => {
    console.log('Expense added, refreshing data:', expense);
    
    // Switch to the expense's month/year so it's visible
    if (expense?.date) {
      const expenseYear = parseInt(expense.date.substring(0, 4));
      const expenseMonth = parseInt(expense.date.substring(5, 7));
      
      // Update year if different
      if (expenseYear !== selectedYear) {
        setSelectedYear(expenseYear);
        // Adjust year range if needed
        if (expenseYear < startYear || expenseYear >= startYear + 5) {
          setStartYear(Math.max(2020, expenseYear - 2));
        }
      }
      
      // Update month if in monthly view
      if (viewMode === 'monthly' && expenseMonth !== selectedMonth) {
        setSelectedMonth(expenseMonth);
      }
      
      toast.info(`Switched to ${expense.date.substring(0, 7)} to show new expense`);
    }
    
    // Force refresh the data
    fetchData(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await expenseAPI.delete(expenseId);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleUpdateExpense = async (expenseId, updateData) => {
    try {
      await expenseAPI.update(expenseId, updateData);
      toast.success('Expense updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(parseInt(value));
  };

  const handleMonthChange = (value) => {
    if (value === 'yearly') {
      setViewMode('yearly');
    } else {
      setViewMode('monthly');
      setSelectedMonth(parseInt(value));
    }
  };

  const handleStartYearChange = (value) => {
    const newStartYear = parseInt(value);
    setStartYear(newStartYear);
    // If current selected year is outside new range, adjust it
    if (selectedYear < newStartYear || selectedYear >= newStartYear + 5) {
      setSelectedYear(newStartYear);
    }
  };

  const handleExport = async (exportType) => {
    setExporting(true);
    try {
      const month = exportType === 'monthly' ? selectedMonth : undefined;
      const response = await expenseAPI.export(selectedYear, month);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      if (exportType === 'monthly') {
        link.setAttribute('download', `expenses_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.xlsx`);
      } else {
        link.setAttribute('download', `expenses_${selectedYear}_annual.xlsx`);
      }
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${exportType === 'monthly' ? getMonthName(selectedMonth) : 'annual'} ${selectedYear} expenses`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Export is a Pro feature. Upgrade to export your expenses.', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/pricing'),
          },
        });
      } else {
        toast.error('Failed to export expenses');
      }
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Calculate monthly total for the selected month
  const monthlyTotal = viewMode === 'monthly'
    ? expenses.reduce((sum, e) => sum + e.amount, 0)
    : summary?.total || 0;

  // Monthly summary for donut chart (filter by selected month)
  const getMonthlySummary = () => {
    if (viewMode !== 'monthly') return {};
    
    const filtered = {};
    expenses.forEach((exp) => {
      const catName = exp.category_name || 'Other';
      if (!filtered[catName]) {
        filtered[catName] = { total: 0, icon: exp.category_icon, count: 0 };
      }
      filtered[catName].total += exp.amount;
      filtered[catName].count += 1;
    });
    return filtered;
  };

  const monthlySummary = getMonthlySummary();
  const monthlyTopCategory = Object.entries(monthlySummary).length > 0
    ? Object.entries(monthlySummary).sort((a, b) => b[1].total - a[1].total)[0][0]
    : null;

  // Generate start year options (current year - 10 to current year)
  const currentYear = getCurrentYear();
  const startYearOptions = Array.from({ length: 15 }, (_, i) => currentYear - 10 + i);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading your finances...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="space-y-6">
          {/* Add Expense */}
          <AddExpense onExpenseAdded={handleExpenseAdded} categories={categories} />

          {/* Year Range & View Selectors */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* Start Year (5-year range) */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">From:</span>
              <Select value={String(startYear)} onValueChange={handleStartYearChange}>
                <SelectTrigger
                  className="w-24 bg-secondary/50 border-white/10"
                  data-testid="start-year-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 max-h-[200px]">
                  {startYearOptions.map((year) => (
                    <SelectItem key={year} value={String(year)}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">to {startYear + 4}</span>
            </div>

            {/* Year Selector */}
            <Select value={String(selectedYear)} onValueChange={handleYearChange}>
              <SelectTrigger
                className="w-24 bg-secondary/50 border-white/10"
                data-testid="year-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {yearRange.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Month Selector */}
            <Select
              value={viewMode === 'yearly' ? 'yearly' : String(selectedMonth)}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger
                className="w-36 bg-secondary/50 border-white/10"
                data-testid="month-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                  <SelectItem key={month} value={String(month)}>
                    {getShortMonthName(month)}
                  </SelectItem>
                ))}
                <SelectItem value="yearly">Yearly Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Export & Refresh Buttons */}
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-white/10 hover:bg-primary/10 hover:text-primary"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('monthly')}
              disabled={exporting || viewMode === 'yearly'}
              className="border-white/10 hover:bg-primary/10 hover:text-primary"
              data-testid="export-monthly-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Export {getShortMonthName(selectedMonth)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('annual')}
              disabled={exporting}
              className="border-white/10 hover:bg-primary/10 hover:text-primary"
              data-testid="export-annual-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export {selectedYear} Annual
            </Button>
          </div>

          {/* Charts */}
          {viewMode === 'monthly' ? (
            <DonutChart
              data={monthlySummary}
              total={monthlyTotal}
              topCategory={monthlyTopCategory}
            />
          ) : (
            <YearlyBarChart
              data={summary?.by_month}
              total={summary?.total || 0}
              year={selectedYear}
            />
          )}

          {/* Expense List */}
          <ExpenseList
            expenses={expenses}
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={(value) => setSelectedCategory(value === 'all' ? null : value)}
            onDelete={handleDeleteExpense}
            onUpdate={handleUpdateExpense}
          />
        </div>
      </main>
    </div>
  );
}
